/**
 * TelnetConsolePanel
 *
 * Correctifs appliqués :
 *  - Backoff exponentiel : 3s → 6s → 12s → 20s (max 4 tentatives)
 *  - closeCodeLabel() : code 1006 traduit en message lisible
 *  - [WS ERROR] supprimé de onerror (toujours suivi de onclose, double message inutile)
 *  - setError("") au succès (effacer erreur précédente)
 *  - Bouton Reconnect remet le compteur à zéro
 *  - Couleur status dans le label
 */
import React, { useEffect, useLayoutEffect, useRef, useState, useMemo, useCallback } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import XtermTerminal from "./XtermTerminal";
import { buildTelnetWsUrl, buildTokenSubprotocols, getToken } from "../../utils/consoleUrl";

const BACKOFF = [3000, 6000, 12000, 20000];
const MAX_RETRIES = 4;

function closeCodeLabel(code) {
  if (code === 1000) return "fermeture normale";
  if (code === 1001) return "page fermée";
  if (code === 1006) return "connexion TCP refusée — GNS3 injoignable ou nœud arrêté";
  if (code === 1011) return "erreur serveur proxy";
  if (code === 4401) return "token invalide";
  return `code ${code}`;
}

const STATUS_COLOR = {
  connected:  "#4caf50",
  connecting: "#ff9800",
  error:      "#f44336",
  closed:     "#9e9e9e",
};

export default function TelnetConsolePanel({ host, port, onStatusChange }) {
  const [status,   setStatus]   = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const wsRef       = useRef(null);
  const termRef     = useRef(null);
  const fitRef      = useRef(null);
  const mountedRef  = useRef(false);
  const timerRef    = useRef(null);
  const statusRef   = useRef("idle");
  const retryCount  = useRef(0);

  const token = useMemo(() => getToken(), []);
  const wsUrl = useMemo(() => {
    if (!host || !port || !token) return "";
    return buildTelnetWsUrl({ host, port });
  }, [host, port, token]);

  const updStatus = useCallback((s, err = "") => {
    statusRef.current = s;
    setStatus(s);
    setErrorMsg(err);
    onStatusChange?.(s, err);
  }, [onStatusChange]);

  // useLayoutEffect garantit que mountedRef=true avant que le rAF de XtermTerminal fire
  useLayoutEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const closeWs = useCallback((force = false) => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    const ws = wsRef.current;
    if (!ws) return;
    wsRef.current = null;
    // Ne pas appeler .close() sur un WS encore en CONNECTING (readyState=0)
    // → provoque "closed before connection established" en StrictMode
    // On l'abandonne : le navigateur le fermera naturellement
    if (ws.readyState === WebSocket.CONNECTING) return;
    try { ws.close(1000, "panel closing"); } catch {}
  }, []);

  useEffect(() => { return () => closeWs(true); }, [closeWs]);

  const connect = useCallback((manualRetry = false) => {
    if (!mountedRef.current) return;
    if (manualRetry) retryCount.current = 0;

    const term = termRef.current;
    if (!term || !wsUrl) { updStatus("error", "URL WebSocket manquante"); return; }

    closeWs();
    updStatus("connecting");

    // SEC-WS-01 : plus aucune trace de l'URL/du token ici -- ni dans le
    // terminal visible (ancien [DBG]), ni dans les DevTools. wsUrl ne
    // contient plus de token (voir buildTelnetWsUrl ci-dessus) ; le seul
    // log restant (host:port) ne permet de reconstituer aucun secret.
    term.writeln("\r\n\x1b[33m[CONNECTING...]\x1b[0m");
    console.debug("[TelnetConsolePanel] connect → host=%s port=%s", host, port);

    const ws = new WebSocket(wsUrl, buildTokenSubprotocols(token));
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;

    ws.onopen = () => {
      // Vérifier que c'est toujours CE ws qui est actif (pas un ws orphelin StrictMode)
      if (wsRef.current !== ws || !mountedRef.current) {
        try { ws.close(1000); } catch {}
        return;
      }
      retryCount.current = 0;
      updStatus("connected");
      setErrorMsg("");
      console.debug("[TelnetConsolePanel] onopen OK");
      try { fitRef.current?.fit(); } catch {}
    };

    ws.onmessage = ({ data }) => {
      if (!mountedRef.current) return;
      let text = null;
      if (data instanceof ArrayBuffer) {
        const u8 = new Uint8Array(data);
        let s = "";
        for (let i = 0; i < u8.length; i += 0x8000)
          s += String.fromCharCode(...u8.subarray(i, i + 0x8000));
        term.write(s);
        text = s;
      } else if (typeof data === "string") {
        term.write(data);
        text = data;
      }
      // Si le backend envoie [ERROR] (TCP refusé / timeout) → pas de retry inutile
      if (text && (text.includes("[ERROR]") || text.includes("[AUTH]"))) {
        retryCount.current = MAX_RETRIES; // épuiser les retries
        const isTcpRefused = text.includes("refus") || text.includes("Connexion refus");
        const isTcpTimeout = text.includes("Timeout") || text.includes("timeout");
        if (isTcpRefused) updStatus("error", `GNS3 TCP refusé sur ${host}:${port} — nœud démarré ?`);
        if (isTcpTimeout) updStatus("error", `GNS3 injoignable sur ${host}:${port} — réseau OK ?`);
      }
    };

    // onerror : capturer le message pour debug (normalement suivi de onclose)
    ws.onerror = (evt) => {
      console.error("[TelnetConsolePanel] onerror", evt);
      term.writeln(`\r\n\x1b[31m[WS ERROR]\x1b[0m Voir console navigateur (F12)`);
    };

    ws.onclose = ({ code, reason, wasClean }) => {
      if (wsRef.current === ws) wsRef.current = null;
      // WS orphelin (abandonné par closeWs en StrictMode) → ignorer silencieusement
      if (!mountedRef.current) return;

      console.debug("[TelnetConsolePanel] onclose code=%d reason=%s wasClean=%s", code, reason, wasClean);

      const label = closeCodeLabel(code);
      const isClean = code === 1000 || code === 1001;
      const isAuth  = code === 4401;

      if (isClean) {
        updStatus("closed");
        term.writeln(`\r\n\x1b[90m[CLOSED] ${label}\x1b[0m`);
        return;
      }
      if (isAuth) {
        updStatus("error", "Token invalide — rechargez la page.");
        term.writeln("\r\n\x1b[31m[AUTH ERROR] Token invalide ou expiré.\x1b[0m");
        return;
      }

      // Erreur réseau — backoff exponentiel
      const retry = retryCount.current;
      const delay = BACKOFF[Math.min(retry, BACKOFF.length - 1)];
      const delaySec = (delay / 1000).toFixed(0);

      term.writeln(`\r\n\x1b[31m[CLOSED code=${code}]\x1b[0m ${label}`);

      if (retry < MAX_RETRIES) {
        retryCount.current += 1;
        term.writeln(`\x1b[33m[INFO]\x1b[0m reconnecting in ${delaySec}s… (${retry + 1}/${MAX_RETRIES})`);
        updStatus("connecting", `Reconnexion ${retry + 1}/${MAX_RETRIES} dans ${delaySec}s…`);
        timerRef.current = setTimeout(() => {
          if (mountedRef.current && statusRef.current !== "connected") connect();
        }, delay);
      } else {
        retryCount.current = 0;
        updStatus("error", `Connexion impossible (${label}). GNS3 démarré ? Nœud actif ?`);
        term.writeln("\r\n\x1b[31m[FAILED]\x1b[0m Trop de tentatives. Cliquez Reconnect.");
      }
    };

    const disp = term.onData((chunk) => {
      try { if (ws.readyState === WebSocket.OPEN) ws.send(chunk); } catch {}
    });
    ws.addEventListener("close", () => disp.dispose());

  }, [wsUrl, closeWs, updStatus]);

  const handleReady = useCallback(({ term, fit }) => {
    termRef.current = term;
    fitRef.current  = fit;
    connect();
  }, [connect]);

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center"
        justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          Telnet {host}:{port} —{" "}
          <b style={{ color: STATUS_COLOR[status] ?? "inherit" }}>{status}</b>
        </Typography>
        <Button size="small" variant="outlined"
          onClick={() => connect(true)}
          disabled={status === "connecting"}>
          Reconnect
        </Button>
      </Stack>

      {errorMsg && (
        <Alert severity={status === "error" ? "error" : "info"}
          sx={{ mb: 1 }} onClose={() => setErrorMsg("")}>
          {errorMsg}
        </Alert>
      )}

      <XtermTerminal key={`${host}:${port}`} onReady={handleReady} />
    </Box>
  );
}
